import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { INR } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Upload, Ban, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import Can from "@/components/Can";
import PriceBook from "@/components/PriceBook";
import ExcelButton from "@/components/ExcelButton";
import { exportExcel, n, stamp } from "@/lib/excel";

const EMPTY = { name: "", unit: "Pcs", rate: 0, enable_multiplier_breakdown: false };
const UNITS = ["Pcs", "Nos", "Roll", "Box", "Pkt", "Ltrs", "Sht", "Kgs", "Dzn", "Set", "Grs", "Mtrs", "Inch"];

export default function Products() {
  const [tab, setTab] = useState("products");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  const filtered = items.filter(p => !ql || [p.name, p.unit, String(p.rate)].some(v => String(v||"").toLowerCase().includes(ql)));
  const excel = () => exportExcel(`Products-${stamp()}`, [{ name: "Products", title: "Product Master", subtitle: q ? `filter: ${q}` : "All products",
    columns: [{ header: "Name", get: p => p.name, width: 30 }, { header: "Unit", get: p => p.unit }, { header: "Rate", get: p => n(p.rate), num: true }, { header: "Multiplier Breakdown", get: p => p.enable_multiplier_breakdown ? "Enabled" : "Off", width: 20 }, { header: "Status", get: p => p.active === false ? "Inactive" : "Active" }],
    rows: filtered }]);

  const load = async () => setItems((await api.get("/products")).data);
  useEffect(()=>{ load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/products/${editId}`, form);
      else await api.post("/products", form);
      toast.success(editId?"Product updated":"Product added");
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (err) { toast.error(fmtError(err.response?.data?.detail)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete product "${p.name}"? Only possible if it was never invoiced.`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(fmtError(err.response?.data?.detail), { duration: 7000 }); }
  };
  const toggleActive = async (p) => {
    const off = p.active !== false;
    if (off && !window.confirm(`Deactivate "${p.name}"?\n\nIt will no longer be selectable on new invoices. Existing invoices and reports are unaffected.`)) return;
    await api.post(`/products/${p.id}/${off ? "deactivate" : "activate"}`); toast.success(off ? "Product deactivated" : "Product reactivated"); load();
  };

  return (
    <div className="space-y-4" data-testid="products-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{fontFamily:'Plus Jakarta Sans'}}>Products</h1>
          <p className="text-sm text-slate-500">Rate master with optional Quantity Multiplier breakdown.</p>
        </div>
        {tab === "products" && <div className="flex items-center gap-2 w-full sm:w-auto">
          <input data-testid="products-search" placeholder="Search name, unit" value={q} onChange={(e)=>setQ(e.target.value)} className="h-9 px-3 border border-slate-300 rounded-md text-sm bg-white outline-none focus:border-blue-600 flex-1 min-w-0 sm:w-56"/>
          <ExcelButton testid="products-excel-btn" onClick={excel}/>
          <Can perm="import_export"><Link to="/data" data-testid="products-import-link" className="h-9 px-3 border border-slate-300 bg-white rounded-md text-xs font-semibold uppercase tracking-wider flex items-center gap-2 hover:bg-slate-50"><Upload size={14}/>Import</Link></Can>
          <Can perm="vouchers"><button data-testid="add-product-btn" onClick={()=>{setForm(EMPTY);setEditId(null);setOpen(true);}}
            className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold uppercase tracking-wider px-3.5 py-2 rounded-md flex items-center gap-2"><Plus size={14}/>Add</button></Can>
        </div>}
      </div>

      <div className="flex gap-2">
        {[["products","Products"],["pricebook","Price Book"]].map(([id,l]) => (
          <button key={id} data-testid={`products-tab-${id}`} onClick={()=>setTab(id)} className={`px-3.5 py-2 text-xs font-semibold uppercase tracking-wider rounded-md ${tab===id ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>

      {tab === "pricebook" && <PriceBook />}

      {tab === "products" && <div className="bg-white border border-slate-200 rounded-md table-scroll">
        <table className="acct-table">
          <thead><tr><th>Name</th><th>Unit</th><th className="num">Rate ₹</th><th>Multiplier Breakdown</th><th></th></tr></thead>
          <tbody>
            {filtered.map((p)=> (
              <tr key={p.id}>
                <td className="font-medium">{p.name}{p.active === false && <span data-testid={`product-inactive-${p.id}`} className="ml-2 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">Inactive</span>}</td>
                <td className="mono text-slate-600">{p.unit}</td>
                <td className="num">{INR(p.rate)}</td>
                <td>{p.enable_multiplier_breakdown
                  ? <span className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">Enabled</span>
                  : <span className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">Off</span>}
                </td>
                <td className="text-right whitespace-nowrap"><Can perm="vouchers">
                  <button data-testid={`product-toggle-${p.id}`} onClick={()=>toggleActive(p)} title={p.active === false ? "Reactivate" : "Deactivate"} className="p-1.5 text-slate-500 hover:text-slate-900">{p.active === false ? <CheckCircle2 size={14}/> : <Ban size={14}/>}</button>
                  <button data-testid={`product-edit-${p.id}`} onClick={()=>{setForm({name:p.name,unit:p.unit,rate:p.rate,enable_multiplier_breakdown:p.enable_multiplier_breakdown});setEditId(p.id);setOpen(true);}} className="p-1.5 text-slate-500 hover:text-slate-900"><Pencil size={14}/></button>
                  <button data-testid={`product-delete-${p.id}`} onClick={()=>del(p)} className="p-1.5 text-slate-500 hover:text-rose-700"><Trash2 size={14}/></button>
                </Can></td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan="5" className="text-center py-8 text-slate-400">{items.length ? "No products match your search." : "No products yet. Add your first item."}</td></tr>}
          </tbody>
        </table>
      </div>}

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4" onClick={()=>setOpen(false)}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={save} className="bg-white rounded-lg border border-slate-300 shadow-xl max-w-lg w-full p-5" data-testid="product-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{editId?"Edit Product":"Add Product"}</h3>
              <button type="button" onClick={()=>setOpen(false)} className="text-slate-400 hover:text-slate-900"><X size={18}/></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Name*</label>
                <input data-testid="product-field-name" required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="mt-1 w-full h-9 px-2.5 border border-slate-300 rounded text-sm outline-none focus:border-blue-600"/>
              </div>
              <div><label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Unit</label>
                <select data-testid="product-field-unit" value={form.unit} onChange={(e)=>setForm({...form,unit:e.target.value})} className="mt-1 w-full h-9 px-2.5 border border-slate-300 rounded text-sm outline-none focus:border-blue-600 bg-white">
                  {UNITS.map(u=><option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div><label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Rate ₹</label>
                <input data-testid="product-field-rate" type="number" step="0.01" value={form.rate} onChange={(e)=>setForm({...form,rate:parseFloat(e.target.value||0)})} className="mt-1 w-full h-9 px-2.5 border border-slate-300 rounded text-sm mono text-right outline-none focus:border-blue-600"/>
              </div>
              <div className="col-span-2 flex items-center gap-2 mt-1 p-3 bg-slate-50 rounded border border-slate-200">
                <input data-testid="product-field-multiplier" id="mult" type="checkbox" checked={form.enable_multiplier_breakdown} onChange={(e)=>setForm({...form,enable_multiplier_breakdown:e.target.checked})}/>
                <label htmlFor="mult" className="text-sm">Enable <b>Quantity Multiplier Breakdown</b> popup on invoice entry</label>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={()=>setOpen(false)} className="h-9 px-4 border border-slate-300 rounded-md text-xs font-semibold uppercase tracking-wider bg-white hover:bg-slate-50">Cancel</button>
              <button type="submit" data-testid="product-save-btn" className="h-9 px-4 bg-slate-900 hover:bg-slate-800 text-white rounded-md text-xs font-semibold uppercase tracking-wider">{editId?"Update":"Save"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
